import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { Reveal } from '../components/Reveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [enquiryForm, setEnquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [serviceForm, setServiceForm] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    details: ''
  });

  const [newsletterEmail, setNewsletterEmail] = useState('');

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${BACKEND_URL}/api/submit-enquiry`, enquiryForm);
      toast.success(response.data.message);
      setEnquiryForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit enquiry. Please try again.');
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${BACKEND_URL}/api/submit-service-request`, serviceForm);
      toast.success(response.data.message);
      setServiceForm({ name: '', email: '', company: '', service: '', details: '' });
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Failed to submit service request. Please try again.');
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter subscription:', newsletterEmail);
    toast.success('Successfully subscribed to our newsletter!');
    setNewsletterEmail('');
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1649886535045-246644def7d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxhaXJjcmFmdCUyMGhhbmdhcnxlbnwwfHx8fDE3NzEwNDU2Njd8MA&ixlib=rb-4.1.0&q=85"
            alt="Contact Us"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal y={30}>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Get in Touch</h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
              Let's discuss how we can support your aviation needs
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Reveal delay={0} className="bg-slate-800 rounded-xl p-8 text-center border border-slate-700 hover:border-slate-600 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="text-slate-300" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Call Us</h3>
              <a href={`tel:${companyInfo.phone}`} className="text-slate-300 hover:text-white transition-colors duration-200">
                {companyInfo.phone}
              </a>
            </Reveal>

            <Reveal delay={0.12} className="bg-slate-800 rounded-xl p-8 text-center border border-slate-700 hover:border-slate-600 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-slate-300" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Email Us</h3>
              <a href={`mailto:${companyInfo.email}`} className="text-slate-300 hover:text-white transition-colors duration-200">
                {companyInfo.email}
              </a>
            </Reveal>

            <Reveal delay={0.24} className="bg-slate-800 rounded-xl p-8 text-center border border-slate-700 hover:border-slate-600 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-700/20">
              <div className="w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-slate-300" size={28} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Visit Us</h3>
              <p className="text-slate-300 text-sm">{companyInfo.address}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* General Enquiry Form */}
            <Reveal x={-40} y={0} className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
              <h2 className="text-3xl font-bold text-white mb-6">General Enquiry</h2>
              <form onSubmit={handleEnquirySubmit} className="space-y-6">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    required
                    value={enquiryForm.name}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={enquiryForm.email}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Phone Number</label>
                  <input
                    type="tel"
                    required
                    value={enquiryForm.phone}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                    placeholder="+91-XXXXXXXXXX"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Subject</label>
                  <input
                    type="text"
                    required
                    value={enquiryForm.subject}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, subject: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Message</label>
                  <textarea
                    required
                    rows="4"
                    value={enquiryForm.message}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200 resize-none"
                    placeholder="Tell us more about your enquiry..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 btn-chrome rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <span>Submit Enquiry</span>
                  <Send size={18} />
                </button>
              </form>
            </Reveal>

            {/* Service Request Form */}
            <Reveal x={40} y={0} className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
              <h2 className="text-3xl font-bold text-white mb-6">Service Request</h2>
              <form onSubmit={handleServiceSubmit} className="space-y-6">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    required
                    value={serviceForm.name}
                    onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={serviceForm.email}
                    onChange={(e) => setServiceForm({ ...serviceForm, email: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Company Name</label>
                  <input
                    type="text"
                    required
                    value={serviceForm.company}
                    onChange={(e) => setServiceForm({ ...serviceForm, company: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                    placeholder="Your Company"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Service Type</label>
                  <select
                    required
                    value={serviceForm.service}
                    onChange={(e) => setServiceForm({ ...serviceForm, service: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                  >
                    <option value="">Select a service</option>
                    <option value="leasing">Aircraft Leasing</option>
                    <option value="spares">Spares & Parts Sales</option>
                    <option value="services">Aviation Services</option>
                    <option value="mro">MRO Services</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Service Details</label>
                  <textarea
                    required
                    rows="4"
                    value={serviceForm.details}
                    onChange={(e) => setServiceForm({ ...serviceForm, details: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200 resize-none"
                    placeholder="Provide details about your service requirements..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 btn-chrome rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2"
                >
                  <span>Request Service</span>
                  <Send size={18} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Subscribe to Our Newsletter
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Stay updated with the latest aviation news, insights, and ONEAVIATION updates
          </p>
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
            <div className="flex gap-3">
              <input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-slate-500 transition-colors duration-200"
                placeholder="Enter your email"
              />
              <button
                type="submit"
                className="px-6 py-3 btn-chrome rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                Subscribe
              </button>
            </div>
          </form>
        </Reveal>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Our Location</h2>
            <div className="aspect-video bg-slate-800 rounded-xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3784.7179676842343!2d73.9947!3d18.4648!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTjCsDI3JzUzLjMiTiA3M8KwNTknNDAuOSJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="ONEAVIATION Location"
              ></iframe>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
